import * as styles from "./RehomingCentrePage.module.scss";

import { type PageProps, graphql } from "gatsby";
import React, { useEffect, useState } from "react";

import HeroImage from "@dogstrust/dt-web-core-lib-front-end/src/components/HeroImage/HeroImage";
import PromotionCardToDisplay from "@dogstrust/dt-web-core-lib-front-end/src/components/SignPostCards/PromotionCards/PromotionCardToDisplay";
import { mapToPromotionCard } from "@dogstrust/dt-web-core-lib-front-end/src/components/SignPostCards/PromotionCards/PromotionCards.utils";
import SocialSharing from "@dogstrust/dt-web-core-lib-front-end/src/components/common/SocialSharing/SocialSharing";
import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import WrappedSection from "@dogstrust/dt-web-core-lib-front-end/src/containers/WrappedSection/WrappedSection";
import IntroRehomingCentre from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroRehomingCentre";
import SectionCentrePromotedDogs from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionCentrePromotedDogs";
import SectionCentreVolunteerRoles from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionCentreVolunteerRoles/SectionCentreVolunteerRoles";
import SectionRehomingCentreKeyDetails from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionRehomingCentreKeyDetails";
import { getRehomingCentreKeyDetailsProps } from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionRehomingCentreKeyDetails/RehomingCentreKeyDetails";
import SectionSignPostCardCarousel from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionSignPostCardCarousel/SectionSignPostCardCarousel";
import SectionStandardSignPostCards from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionStandardSignPostCards";
import {
	useDogSearchApi,
	usePageManager,
} from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/uk/components/Layout/Layout";

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
import { Colours } from "@dogstrust/dt-web-core-lib-front-end/src/utils/colours";
import { getImagePath } from "@dogstrust/dt-web-core-lib-front-end/src/utils/images";
import {
	mapCentreArticleToSignpostCard,
	mapPromotedItemToSignpostCard,
} from "@dogstrust/dt-web-core-lib-front-end/src/utils/mappers";
import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";

interface Props extends PageProps {
	data: Queries.RehomingCentreQueryQuery;
}

export const Head: React.FC<Props> = ({ data: { nodeCentre } }) => {
	const {
		title,
		path,
		field_meta_tags,
		field_phone_number,
		field_address_line_one,
		field_city,
		field_postcode,
		relationships: { field_centre_images },
	} = nodeCentre;

	const schema = {
		"@context": "https://schema.org",
		"@type": "AnimalShelter",
		name: `Dogs Trust ${title}`,
		email: "info@dogstrust.org.uk",
		description: `Contact details and opening hours for the Dogs Trust Rehoming Centre in ${title}. Come and say hello!`,
		url: process.env.GATSBY_DOMAIN_NAME + path.alias,
		image: `${getImagePath(
			"400x300",
			field_centre_images[0].field_media_image?.uri,
		)}`,
		telephone: field_phone_number,
		address: {
			"@type": "PostalAddress",
			streetAddress: field_address_line_one,
			addressLocality: field_city,
			postalCode: field_postcode,
		},
		parentOrganization: {
			name: "Dogs Trust",
			"@type": "NGO",
			url: process.env.GATSBY_DOMAIN_NAME,
		},
	};
	return (
		<DefaultHead pageUrl={path.alias}>
			<SEO
				{...getSeoProps(field_meta_tags, {
					title: `Dog Rescue, Rehoming & Adoption ${title} | Dogs Trust`,
					description: `Contact details and opening hours for the Dogs Trust Rehoming Centre in ${title}. Come and say hello!`,
					canonical_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					og_type: "article",
					og_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					og_title: `Dog Rescue, Rehoming & Adoption ${title} | Dogs Trust`,
					og_description: `Contact details and opening hours for the Dogs Trust Rehoming Centre in ${title}. Come and say hello!`,
					og_image_url: `${getImagePath(
						"400x300",
						field_centre_images[0].field_media_image?.uri,
					)}`,
					twitter_cards_type: "summary_large_image",
					twitter_cards_description: `Contact details and opening hours for the Dogs Trust Rehoming Centre in ${title}. Come and say hello!`,
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: `Dog Rescue, Rehoming & Adoption ${title} | Dogs Trust`,
					twitter_cards_page_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					twitter_cards_image: `${getImagePath(
						"400x300",
						field_centre_images[0].field_media_image?.uri,
					)}`,
					schemaMarkup: schema,
				})}
			/>
		</DefaultHead>
	);
};

export const RehomingCentrePage: React.FC<Props> = ({
	data: { nodeCentre, nodeSection },
}) => {
	const {
		title,
		path,
		breadcrumbs,
		field_centre_lead_paragraph,
		field_centre_intro_text,
		field_is_virtual_centre,
		centreArticles,
		centreVacancies,
		relationships: { field_centre_intro_image, field_promotion_blocks },
	} = nodeCentre;

	const [hasInit, setHasInit] = useState(false);
	const { onInitFilters } = useDogSearchApi();

	const { usePagination } = usePageManager();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		usePagination(false);
	}, []);

	useEffect(() => {
		if (!hasInit) {
			const mappedCentres: DogsTrust.RehomingCentreLocation[] = [
				nodeCentre,
			].map((centre) => ({
				title: centre.title,
				centreCode: centre.relationships.field_centre_code.name,
				latitude: Number.parseFloat(centre.field_latitude),
				longitude: Number.parseFloat(centre.field_longitude),
			}));
			onInitFilters(
				false,
				false,
				mappedCentres,
				nodeCentre.relationships.field_centre_code.name,
			);
			setHasInit(true);
		}
	}, [hasInit, nodeCentre, onInitFilters]);

	return (
		<Layout breadcrumbs={breadcrumbs ? [...breadcrumbs] : []}>
			<div className={styles.rehomingcentrepage}>
				<IntroRehomingCentre
					title={nodeCentre.title}
					location={nodeCentre.field_city}
					introText={
						"If you are thinking about getting a new doggy addition for your family you have come to the right place!"
					}
					images={nodeCentre.relationships.field_centre_images?.map(
						(image) => ({
							url: image.field_media_image.uri,
							alt: image.field_media_image.alt,
						}),
					)}
				/>
				<SectionRehomingCentreKeyDetails
					{...getRehomingCentreKeyDetailsProps(nodeCentre)}
				/>
				<WrappedSection
					sectionName="RehomingCentrePageDetails"
					sectionColour={Colours.WHITE}
				>
					<div className={styles.aboutandpromo}>
						<div className={styles.about}>
							<div className={styles.heading}>
								{field_is_virtual_centre ? (
									<h2>{`About the ${title} virtual centre`}</h2>
								) : (
									<h2>{`About the ${title} centre`}</h2>
								)}
							</div>
							{field_centre_lead_paragraph?.value?.length > 0 && (
								<div
									className={styles.leadpara}
									// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
									dangerouslySetInnerHTML={{
										__html: field_centre_lead_paragraph.value,
									}}
								/>
							)}
							{field_centre_intro_image?.field_media_image && (
								<div className={styles.imagewrapper}>
									<HeroImage
										src={field_centre_intro_image?.field_media_image.uri}
										alt={field_centre_intro_image?.field_media_image.alt}
									/>
								</div>
							)}
							{field_centre_intro_image?.field_caption && (
								<div className={styles.imagecaption}>
									{field_centre_intro_image.field_caption}
								</div>
							)}
							{field_centre_intro_text?.value && (
								<div
									className={styles.intropara}
									// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
									dangerouslySetInnerHTML={{
										__html: field_centre_intro_text.value,
									}}
								/>
							)}
						</div>
						<div className={styles.promosection}>
							{field_promotion_blocks?.map((promo) => (
								<PromotionCardToDisplay
									key={promo.field_title_front_end}
									{...mapToPromotionCard(
										// biome-ignore lint/suspicious/noExplicitAny: Required for casting
										promo as any as Queries.node__promotion,
									)}
								/>
							))}
						</div>
					</div>
				</WrappedSection>
				<SectionCentrePromotedDogs
					centre={nodeCentre.title}
					centreCode={nodeCentre.relationships?.field_centre_code?.name}
					currentDogId=""
				/>
				{centreArticles.length > 0 && (
					<SectionSignPostCardCarousel
						title={"Our stories"}
						intro={"A collection of updates about life around the centre."}
						items={mapCentreArticleToSignpostCard(
							// biome-ignore lint/suspicious/noExplicitAny: Required for casting
							centreArticles as any as Queries.node__centre_article[],
						)}
						backgroundColour={Colours.SANDY_FUR}
						customBorder="CurvedBorderBottom"
					/>
				)}

				<SectionStandardSignPostCards
					key={nodeSection.title}
					title={nodeSection.field_front_end_title}
					link={{ ...nodeSection.field_link }}
					items={nodeSection.promotedLinks.map(mapPromotedItemToSignpostCard)}
					withPageBreak={false}
					variant="CurvedBorderTop"
				/>
				{centreVacancies?.length > 0 && (
					<SectionCentreVolunteerRoles
						centreName={nodeCentre.title}
						title={"Volunteering opportunites"}
						introText={
							"Looking for the perfect volunteer opportunity? We've got you covered - from hands on dog walkers to shop assistants and so much more!"
						}
						vacancies={[...centreVacancies]}
					/>
				)}
				<WrappedSection
					sectionName="ArticlePageSocialMedia"
					sectionColour={Colours.WHITE}
				>
					<SocialSharing
						title="If you liked this page, tell the world!"
						shareBodyText="I saw this on the Dogs Trust’s website and thought of you"
						sharePath={process.env.GATSBY_DOMAIN_NAME + path.alias}
					/>
				</WrappedSection>
			</div>
		</Layout>
	);
};
export default RehomingCentrePage;

export const query = graphql`
	
    query RehomingCentreQuery($path: String) {
        nodeCentre(path: { alias: { eq: $path } }) {
            ...RehomingCentreData
        }

        nodeSection(title: { eq: "Rehoming-Centre-Articles" }) {
            title
            field_front_end_title
            field_min_items_to_display
            field_max_items_to_display
            field_link {
                title
                uri
            }
            field_section_short_description {
                processed
                value
            }
            relationships {
                field_method_of_ordering_items {
                    name
                }
                field_component_type {
                    name
                }
                field_extra_section_info {
                    field_extra_information {
                        value
                    }
                }
            }
            promotedLinks {
                ...PromoteToUnionData
            }
        }
    }
`;

export const fragment = graphql`
    fragment RehomingCentreData on node__centre {
        id
        title
        breadcrumbs {
            ...BreadCrumb
        }
        moderation_state
        path {
            alias
        }
        field_address_line_one
        field_address_line_two
        field_city
        field_county
        field_dog_adoption_form
        field_facebook_url
        field_google_maps_link
        field_instagram_url
        field_is_virtual_centre
        field_latitude
        field_linkedin_url
        field_longitude
        field_opening_times_text
        field_phone_number
        field_postcode
        field_twitter_url
        field_centre_lead_paragraph {
            value
        }
        field_centre_intro_text {
            value
        }
        field_wheelchair_text
        field_meta_tags {
            ...MetaTags
        }
        centreArticles {
            ...RehomingCentreArticleData
        }
        centreVacancies {
            ...VolunteerVacancyData
        }
        relationships {
            field_centre_code {
                name
            }
            field_centre_images {
                field_media_image {
                    uri
                    alt
                }
            }
            field_centre_intro_image {
                field_caption
                field_media_image {
                    uri
                    alt
                }
            }
            field_promotion_blocks {
                field_title_front_end
                field_text
                field_promo_link {
                    uri
                    title
                }
                relationships {
                    field_promo_image {
                        field_media_image {
                            uri
                            alt
                        }
                    }
                    field_promotion_type {
                        name
                    }
                }
            }
        }
    }
`;
